/**
 * Inventory view component.
 * @module components/View/InventoryView
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';
import {
  Checkbox,
  Input,
  Label,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { searchContent, updateContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import circlePlusSVG from '@plone/volto/icons/add.svg';
import circleMinusSVG from '@plone/volto/icons/remove.svg';

// @search only exact-matches a single portal_type, so each type is
// fetched separately, with a b_size well above the number of items that
// will ever match, so there's just the one request each — no paging.
const BATCH_SIZE = 10000;

const IN_STOCK_ONLY_COOKIE = 'inventory-in-stock-only';

// Reads the "in stock only" toggle from its cookie (defaulting to on when
// there's no stored value yet), tolerating cookies being disabled/missing.
const readInStockOnly = () => {
  try {
    const stored = new Cookies().get(IN_STOCK_ONLY_COOKIE);
    return stored === undefined ? true : stored === 'true';
  } catch (error) {
    return true;
  }
};

// EUR has no live exchange rate of its own yet, so it's shown as a flat
// $5 markup over the USD price.
const formatAmount = (amount, currency) => {
  const value = Number(amount) || 0;
  const converted = currency === 'EUR' ? value + 5 : value;
  const symbol = currency === 'EUR' ? '€' : '$';
  return `${symbol} ${converted.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Inventory view component class.
 * @function InventoryView
 * @returns {string} Markup of the component.
 */
const InventoryView = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currency = useSelector((state) => state.currency);
  const [items, setItems] = useState([]);

  useEffect(() => {
    const fetchAllOfType = async (portal_type) => {
      const result = await dispatch(
        searchContent(
          '',
          { portal_type, b_size: BATCH_SIZE },
          `inventory-${portal_type}`,
        ),
      );
      return result?.items || [];
    };

    (async () => {
      const [sets, sizes] = await Promise.all([
        fetchAllOfType('Set'),
        fetchAllOfType('Size'),
      ]);
      // Only exact matches are possible server-side, so "price > 0" is
      // filtered here instead.
      setItems(
        [...sets, ...sizes].filter((item) => (Number(item.price) || 0) > 0),
      );
    })();
  }, [dispatch]);

  // Filtering
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [size, setSize] = useState('');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [inStockOnly, setInStockOnly] = useState(readInStockOnly);

  const codeRef = useRef(null);
  const nameRef = useRef(null);
  const sizeRef = useRef(null);
  const amountRef = useRef(null);
  const priceRef = useRef(null);

  useEffect(() => {
    try {
      new Cookies().set(IN_STOCK_ONLY_COOKIE, inStockOnly, {
        path: '/',
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      });
    } catch (error) {
      // Ignore (e.g. cookies disabled).
    }
  }, [inStockOnly]);

  const filterField = (list, field, value) =>
    value === ''
      ? list
      : list.filter(
          (item) =>
            item[field] !== undefined &&
            item[field] !== null &&
            String(item[field]).toLowerCase().indexOf(value.toLowerCase()) !==
              -1,
        );

  let filtered = items;
  filtered = filterField(filtered, 'code', code);
  filtered = filterField(filtered, 'name', name);
  filtered = filterField(filtered, 'title', size);
  filtered = filterField(filtered, 'inventory', amount);
  filtered = filterField(filtered, 'price', price);
  if (inStockOnly) {
    filtered = filtered.filter((item) => (Number(item.inventory) || 0) > 0);
  }

  // Sorting
  const [sort, setSort] = useState('code');
  const [dir, setDir] = useState('asc');

  filtered = [...filtered].sort((a, b) =>
    (dir === 'asc' ? a[sort] > b[sort] : a[sort] < b[sort]) ? 1 : -1,
  );

  const onSort = (value) => {
    if (sort === value) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setDir('asc');
    }
  };

  const SortIcon = () => (
    <Icon
      name={dir === 'desc' ? upSVG : downSVG}
      className="icon"
      size="20px"
    />
  );

  // Adjusts a single item's inventory by +1/-1: updates the backend and,
  // optimistically, this item in local state — no need to re-fetch the
  // whole list for a single-field change like this.
  const onAdjustInventory = (item, delta) => {
    const inventory = Math.max(0, (Number(item.inventory) || 0) + delta);
    setItems((current) =>
      current.map((current_item) =>
        current_item['@id'] === item['@id']
          ? { ...current_item, inventory }
          : current_item,
      ),
    );
    dispatch(updateContent(flattenToAppURL(item['@id']), { inventory }));
  };

  return (
    <article id="inventory">
      <div className="button-toolbar">
        <div className="button-toolbar-title">
          Inventory <Label circular>{filtered.length}</Label>
        </div>
        <Checkbox
          label="In stock only"
          className="float-right"
          checked={inStockOnly}
          onChange={() => setInStockOnly(!inStockOnly)}
        />
      </div>
      <div className="view-padding">
        <div className="scrollable-table">
          <Table striped celled selectable>
            <TableHeader>
              <TableRow className="tools-header">
                <TableHeaderCell onClick={() => onSort('code')} width="2">
                  Code {sort === 'code' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('name')} width="5">
                  Name {sort === 'name' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('title')} width="1">
                  Size {sort === 'title' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('inventory')} width="2">
                  Amount {sort === 'inventory' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('price')} width="2">
                  Price {sort === 'price' && <SortIcon />}
                </TableHeaderCell>
              </TableRow>
              <TableRow className="tools-filter">
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={code}
                      placeholder="Filter code"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={codeRef}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                    />
                    {code && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setCode('');
                          codeRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={name}
                      placeholder="Filter name"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={nameRef}
                      onChange={(e) => setName(e.target.value)}
                    />
                    {name && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setName('');
                          nameRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={size}
                      placeholder="Filter size"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={sizeRef}
                      onChange={(e) => setSize(e.target.value)}
                    />
                    {size && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setSize('');
                          sizeRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={amount}
                      placeholder="Filter amount"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      ref={amountRef}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                    {amount && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setAmount('');
                          amountRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={price}
                      placeholder="Filter price"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      ref={priceRef}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                    {price && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setPrice('');
                          priceRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => (
                <TableRow
                  key={item['@id']}
                  className="clickable-row"
                  onClick={() => history.push(flattenToAppURL(item['@id']))}
                >
                  <TableCell>{item.code}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>
                    <div className="quantity-input">
                      <button
                        type="button"
                        className="circular-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAdjustInventory(item, -1);
                        }}
                      >
                        <Icon name={circleMinusSVG} size="18px" />
                      </button>
                      <span
                        style={{
                          flex: '1 1 auto',
                          textAlign: 'center',
                          ...((Number(item.inventory) || 0) > 0
                            ? { color: '#4CAF50' }
                            : {}),
                        }}
                      >
                        {Number(item.inventory) || 0}
                      </span>
                      <button
                        type="button"
                        className="circular-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAdjustInventory(item, 1);
                        }}
                      >
                        <Icon name={circlePlusSVG} size="18px" />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell>{formatAmount(item.price, currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </article>
  );
};

export default InventoryView;
